import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import type { TableHeaderCellVariant } from "./variants";
import type { IntrinsicComponent } from "@/types/components";
import { computed, defineComponent } from "vue";
import { useCtx } from "@/composables/context";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { tableCellVariant, tableHeaderCellVariant, tableRowVariant } from "./variants";

export const tableProps = {
  density: p.string<TableHeaderCellVariant["density"]>("default"),
  striped: p.boolean(),
} satisfies ComponentPropsOptions;

type TableProps = ExtractPublicPropTypes<typeof tableProps>;

export const tableCtx = useCtx<TableProps>();

const Root = defineComponent({
  name: "DataTable",
  props: tableProps,
  setup(props, { slots }) {
    tableCtx.provide(props);

    return () => (
      <div class="w-full">
        <table class="bg-table w-full caption-bottom">{slots.default?.()}</table>
      </div>
    );
  },
});

const Header = defineComponent({
  name: "TableHeader",
  props: tableProps,
  setup(_, { slots }) {
    return () => <thead class="[&_tr]:border-b">{slots.default?.()}</thead>;
  },
});

const Row = defineComponent({
  name: "TableRow",
  props: {
    ...tableProps,
    onClick: p.func(),
  },
  setup(props, { slots }) {
    const variants = computed(() => tableRowVariant());

    return () => (
      <tr
        class={cn(variants.value, props.onClick && "cursor-pointer")}
        tabindex={props.onClick ? 0 : undefined}
        onClick={props.onClick}
      >
        {slots.default?.()}
      </tr>
    );
  },
});

const HeadRow = defineComponent({
  name: "TableRow",
  setup(_, { slots }) {
    return () => <tr class="bg-table">{slots.default?.()}</tr>;
  },
});

const HeaderCell = defineComponent({
  name: "TableHeaderCell",
  props: tableProps,
  setup(props, { slots }) {
    const tableProps = tableCtx.inject(props);

    const variants = computed(() =>
      tableHeaderCellVariant({
        density: tableProps.density,
      })
    );

    return () => (
      <th class={variants.value} role="columnheader">
        {slots.default?.()}
      </th>
    );
  },
});

const Body = defineComponent({
  name: "TableBody",
  props: tableProps,
  setup(_, { slots }) {
    return () => (
      <tbody class="[&_tr:last-child]:border-0 [&_tr:last-child]:border-border">
        {slots.default?.()}
      </tbody>
    );
  },
});

const Cell = defineComponent({
  name: "TableCell",
  props: tableProps,
  setup(props, { slots, attrs }) {
    const tableProps = tableCtx.inject(props);
    const variants = computed(() => tableCellVariant({ density: tableProps.density }));

    return () => <td class={cn(variants.value, attrs.class ?? "")}>{slots.default?.()}</td>;
  },
}) as IntrinsicComponent<"td", typeof tableProps>;

export default {
  Root,
  Header,
  HeaderCell,
  Body,
  Row,
  Cell,
  HeadRow,
};
